import React, { useRef, useState, useEffect } from "react";
import { useControls, folder } from "leva";
import { useFrame } from "@react-three/fiber";
import { useSpring } from "react-spring";
import * as THREE from "three";
import { isMobile } from "react-device-detect";
// * State
import useCustomizerStore from "../../../state/customizer";
import useTransitionStore from "../../../state/transitions";
// * Components
import CarFlip from "./CarFlip/CarFlipPlatform";
import Hotspots from "../Hotspots";
import ToyotaCrown from "./ToyotaCrown";
import GradientMask from "./CarFlip/GradientMask";
import Effects from "./Effects";
// * Util
import {
  fetchCustomizerData,
  getSelectedColorIndex,
} from "../../../util/customizerData";
import { clearSelectedHotspot } from "../../../util/hotspotData";

const MODEL_ROTATION_STEP = (2 * Math.PI) / 3;
const getQuaternionForModel = (modelIndex) => {
  const quaternion = new THREE.Quaternion();
  quaternion.setFromEuler(
    new THREE.Euler(-MODEL_ROTATION_STEP * modelIndex, 0, 0)
  );
  return quaternion;
};

const ToyotaCrownContainer = () => {
  const mesh = useRef();
  const rotX = useRef(0);
  const firstRender = useRef(true);
  const [showCars, setShowCars] = useState(true);
  const model = useCustomizerStore((s) => s.model);
  const ShowCar = useTransitionStore((state) => state.ShowCar);
  const ShowHotspots = useTransitionStore((s) => s.ShowHotspots);
  const DisplayMode = useTransitionStore((s) => s.DisplayMode);
  const OrbitModeActive = useTransitionStore((s) => s.OrbitModeActive);
  const SuspensionMode = useTransitionStore((s) => s.SuspensionMode);
  const setAllTrimColors = useCustomizerStore((s) => s.setAllTrimColors);
  const setFutureSelectedHotspotTitle = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotTitle
  );
  const setFutureSelectedHotspotDescription = useCustomizerStore(
    (s) => s.setFutureSelectedHotspotDescription
  );
  const setSelectedDisclaimer = useCustomizerStore(
    (s) => s.setSelectedDisclaimer
  );

  const [destQuaternion, setDest] = useState(new THREE.Quaternion(0, 0, 0, 1));

  const [currentQuaternion, setCurrent] = useState(
    new THREE.Quaternion(0, 0, 0, 1)
  );

  const prevModel = useRef(null);
  useEffect(() => {
    if (firstRender.current === true) {
      firstRender.current = false;
      const initialQuaternion = getQuaternionForModel(model);
      rotX.current = -MODEL_ROTATION_STEP * model;
      if (mesh.current) {
        mesh.current.quaternion.copy(initialQuaternion);
      }
      setCurrent(new THREE.Quaternion().copy(initialQuaternion));
      setDest(new THREE.Quaternion().copy(initialQuaternion));
      prevModel.current = model;
    } else {
      if (model > prevModel.current) {
        if (prevModel.current === 0 && model === 2) {
          handleFlip("up");
        } else {
          handleFlip("down");
        }
      } else if (model === 0 && prevModel.current === 2) {
        handleFlip("down");
      } else if (model < prevModel.current) {
        handleFlip("up");
      } else {
        console.log(`miss flip`);
      }
      prevModel.current = model;
    }
  }, [model]);

  useEffect(() => {
    const data = fetchCustomizerData();

    setAllTrimColors(
      data[0].colors[getSelectedColorIndex(0, "exterior")],
      data[1].colors[getSelectedColorIndex(1, "exterior")],
      data[2].colors[getSelectedColorIndex(2, "exterior")]
    );
  }, []);

  useEffect(() => {
    clearSelectedHotspot();
    setFutureSelectedHotspotDescription("");
    setFutureSelectedHotspotTitle("");
    setSelectedDisclaimer("");
    // setHotspotLocations(HotspotLocations(DisplayMode));
  }, [DisplayMode]);

  const { rotation, X, Y, Z } = useControls({
    car: folder({
      rotation: {
        value: Math.PI * 1.5,
        min: 0,
        max: Math.PI * 2,
        step: 0.001,
      },

      X: {
        value: 0,
        min: -10,
        max: 10,
        step: 0.001,
      },
      Y: {
        value: -0.95,
        min: -10,
        max: 10,
        step: 0.001,
      },
      Z: {
        value: 1.25,
        min: -10,
        max: 10,
        step: 0.001,
      },
    }),
  });
  const { flipSpeed } = useControls({
    carFlipper: folder({
      flipSpeed: {
        value: 0.25, //.25
        min: 0,
        max: 1,
        step: 0.001,
      },
    }),
  });

  const step = useSpring({
    from: { t: 0 },
    to: { t: flipSpeed },
    reset: true,
    config: {
      mass: 1,
      tension: 100,
      friction: 300,
    },
  });

  const handleFlip = (direction) => {
    if (direction === "up") {
      rotX.current = rotX.current + (2 * Math.PI) / 3;
    } else if (direction === "down") {
      rotX.current = rotX.current - (2 * Math.PI) / 3;
    }
    const x = rotX.current;
    const y = 0;
    const z = 0;

    const euler = new THREE.Euler(x, y, z);
    const quaternion = new THREE.Quaternion();

    quaternion.setFromEuler(euler);

    setCurrent(new THREE.Quaternion().copy(mesh.current.quaternion));
    setDest(quaternion);
  };

  useFrame(() => {
    mesh.current.quaternion.slerp(destQuaternion, step.t.value);
  });

  const positions = [
    [0, 1.565, -0.22],
    [X, -0.58, 1.47],
    [X, Y, -Z],
  ];

  useEffect(() => {
    if (OrbitModeActive) {
      setShowCars(false);
    } else {
      setTimeout(() => {
        setShowCars(true);
      }, 1500);
    }
  }, [OrbitModeActive]);

  console.log(`car index`);

  return (
    <group>
      <group
        visible={ShowCar && !SuspensionMode}
        // visible={false}
        position={[0, 0, 0.2]}
        ref={mesh}
      >
        <group>
          {positions.map((position, i) => (
            <group
              key={("crown_", i)}
              visible={(i === model && !showCars) || showCars}
              position={position}
              rotation={[((2 * Math.PI) / 3) * i, rotation, 0]}
            >
              <ToyotaCrown modelIndex={i} />
              <group>
                {ShowHotspots && i === model && OrbitModeActive && <Hotspots />}
              </group>
            </group>
          ))}
        </group>
        <CarFlip />
      </group>
      {!isMobile && DisplayMode !== "interior" && <Effects />}
      {/* <GradientMask /> */}
    </group>
  );
};

export default React.memo(ToyotaCrownContainer);
